/**
 * Created by Ning Jiang on 10/4/2018.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TweenLite} from "gsap";
import {TombstoneGameConfig} from "../TombstoneGameConfig";

export class TombstoneMultiplierView extends BaseView {

    private readonly _maxWidth:number = 260;
    private readonly _minLetterSpacing:number = -23;
    private readonly _numberStyle = {
        fontName : "Multipliers",
        fontSize : 38,
        tint : parseInt(("#ffffff").replace(/^#/, ''), 16)
    };

    private _container:PIXI.Container;
    private _background:TimelineSprite;
    private _xText:PIXI.BitmapText;
    private _invisibleNumber:PIXI.BitmapText;
    private _numberTexts:PIXI.BitmapText[];

    constructor() {
        super();
    }

    protected initAnimations():void {
        this._container = new PIXI.Container();
        this._container.position.set(1010, 105);
        this._container.visible = false;
        const layer:ScalableLayer = StageManager.getLayer(TombstoneGameConfig.instance.LAYERS.MULTIPLIER.name);
        layer.addChild(this._container);

        this._background = new TimelineSprite(GameResources.getTextures("multiplierBg"));
        this._background.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._background.anchor.set(0.5, 0.5);
        this._background.scale.set(2, 2);
        this._background.hide();
        this._container.addChild(this._background);

        this._xText = new PIXI.BitmapText("x", this._numberStyle);
        (<PIXI.Point>this._xText.anchor).set(0.5, 0.5);
        this._container.addChild(this._xText);

        this._invisibleNumber = new PIXI.BitmapText("x1", this._numberStyle);
        (<PIXI.Point>this._invisibleNumber.anchor).set(0.5, 0.5);
        this._invisibleNumber.visible = false;
        this._container.addChild(this._invisibleNumber);

        this._numberTexts = [];

        //test
        // this.showMultiplier(55, false);
    }

    public showMultiplier(number:number, animate:boolean):void {
        this._invisibleNumber.text = `x${number}`;
        // When updating multiplier when the number is already showing, we don't need to clear the numbers. only clear when hide.
        number.toString().split("").forEach((n:string, index:number) => {
            if(this._numberTexts[index] == null) {
                this._numberTexts[index] = new PIXI.BitmapText(n, this._numberStyle);
                (<PIXI.Point>this._numberTexts[index].anchor).set(0.5, 0.5);
            }
            this._numberTexts[index].text = n;
            this._container.addChild(this._numberTexts[index]);
        });
        this.organize(number);
        if(animate) {
            this._background.getAnimationAutoShowHide();
            TweenLite.fromTo(this._xText.scale, 0.2, {x : 0.5, y : 0.5}, {x : 1, y : 1});
        }
        this._container.alpha = 1;
        this._container.visible = true;
    }

    public hideMultiplier():void {
        TweenLite.to(this._container, 0.3, {
            alpha : 0, delay : 0.3, onComplete : () => {
                this._container.visible = false;
                this._numberTexts.forEach((number:PIXI.BitmapText) => {
                    this._container.removeChild(number);
                });
                this._numberTexts.length = 0;
            }
        });
    }

    private organize(number:number):void {
        this._invisibleNumber.letterSpacing = 0;
        if(number >= 10) {
            this._invisibleNumber.updateTransform();
            if(this._invisibleNumber.textWidth > this._maxWidth) {
                const numSpace:number = number.toString().length; // with x in it. so removed -1.
                this._invisibleNumber.letterSpacing = Math.floor((this._maxWidth - this._invisibleNumber.textWidth) / numSpace);
            }
        }
        this._invisibleNumber.letterSpacing = Math.min(this._invisibleNumber.letterSpacing, this._minLetterSpacing);
        this._invisibleNumber.updateTransform();
        const totalWidth:number = this._invisibleNumber.textWidth;
        this._xText.x = -totalWidth / 2 + this._xText.textWidth / 2;
        let numberX:number = -totalWidth / 2 + this._xText.textWidth + this._invisibleNumber.letterSpacing;
        this._numberTexts.forEach((numberText:PIXI.BitmapText) => {
            numberText.x = numberX + numberText.textWidth / 2;
            numberX += numberText.textWidth + this._invisibleNumber.letterSpacing;
        });

        const scaleFactor:number = MathHelper.roundToDecimals((this._invisibleNumber.letterSpacing - this._minLetterSpacing) / this._invisibleNumber.textHeight / 2, 2) + 1;
        this._container.scale.set(scaleFactor, scaleFactor);
    }
}